import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useLocation } from "react-router-dom";
import apiUrl from "../../Apiurl";
import BlockSkeleton from "../../interfaccia/loading/BlockSkeleton";
import { buildLogisticaAdapters } from "./adapters";

export const configurationUrl = `${apiUrl}/manutenzione/logistica-configurazione`;
export const LogisticaConfigurationContext = createContext(null);
const Context = LogisticaConfigurationContext;
export const canReadConfiguration = user => Boolean(user?.permessiAPI?.["read:logistica"] || user?.permessiAPI?.["read:database"]);

export function LogisticaConfigurationProvider({ utente, children }) {
  const { getAccessTokenSilently } = useAuth0();
  const location = useLocation();
  const canRead = canReadConfiguration(utente);
  const [snapshot, setSnapshot] = useState(null);
  const [error, setError] = useState("");
  const requestRef = useRef(null);
  const snapshotRef = useRef(null);
  const mounted = useRef(true);
  const acceptSnapshot = useCallback(next => {
    const previous = snapshotRef.current;
    if (previous?.mode === "current" && next?.mode === "current") {
      const a = next.counter; const b = previous.counter;
      if (typeof a === "string" && typeof b === "string" && (a.length < b.length || (a.length === b.length && a < b))) return;
    }
    requestRef.current?.abort();
    snapshotRef.current = next; setSnapshot(next); setError("");
  }, []);
  const refresh = useCallback(async () => {
    if (!canRead) return;
    requestRef.current?.abort();
    const controller = new AbortController(); requestRef.current = controller;
    try {
      const token = await getAccessTokenSilently();
      if (controller.signal.aborted) return;
      const current = snapshotRef.current;
      const response = await fetch(configurationUrl, { signal: controller.signal, headers: {
        Authorization: `Bearer ${token}`, ...(current ? { "If-None-Match": `"logistica-${current.revision}"` } : {}),
      } });
      if (response.status !== 304 && !response.ok) {
        const body = await response.json(); throw new Error(body.error || `HTTP ${response.status}`);
      }
      const next = response.status === 304 ? current : await response.json();
      if (controller.signal.aborted || requestRef.current !== controller || !mounted.current) return;
      acceptSnapshot(next);
    } catch (err) {
      if (!controller.signal.aborted && requestRef.current === controller && mounted.current) setError(err.message);
    }
  }, [canRead, getAccessTokenSilently, acceptSnapshot]);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; requestRef.current?.abort(); };
  }, []);
  useEffect(() => {
    snapshotRef.current = null; setSnapshot(null); setError(""); requestRef.current?.abort();
  }, [utente?.username, canRead]);
  useEffect(() => { refresh(); return () => requestRef.current?.abort(); }, [refresh, location.pathname, utente?.username]);
  useEffect(() => {
    const onFocus = () => { if (document.visibilityState !== "hidden") refresh(); };
    window.addEventListener("focus", onFocus); document.addEventListener("visibilitychange", onFocus);
    return () => { window.removeEventListener("focus", onFocus); document.removeEventListener("visibilitychange", onFocus); };
  }, [refresh]);
  useEffect(() => {
    if (!canRead) return undefined;
    const interval = window.setInterval(() => {
      if (document.visibilityState !== "hidden") refresh();
    }, 60000);
    return () => window.clearInterval(interval);
  }, [canRead, refresh]);
  const adapters = useMemo(() => snapshot ? buildLogisticaAdapters(snapshot.configuration, snapshot.displayAliases) : {}, [snapshot]);
  const value = useMemo(() => ({ snapshot, error, refresh, acceptSnapshot, canRead, ...adapters }), [snapshot, error, refresh, acceptSnapshot, canRead, adapters]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
}
export function useLogisticaConfiguration() {
  const context = useContext(Context);
  if (!context) throw new Error("Provider configurazione logistica assente");
  return context;
}
export function withLogisticaConfiguration(Component) {
  function ConfigurationBoundary(props) {
    const { snapshot, error, refresh, canRead } = useLogisticaConfiguration();
    if (!canRead) return <p className="pa3">Per questa schermata serve read:logistica oppure read:database.</p>;
    if (!snapshot) return <div className="pa3" aria-busy={!error}>
      {error ? <><p role="alert">{error}</p><button onClick={refresh}>Riprova</button></> : <BlockSkeleton height={180} aria-label="Caricamento configurazione logistica" />}
    </div>;
    return <>{error && <div className="pa2" role="alert">Aggiornamento configurazione fallito: {error}. {snapshot.mode === "current" ? "Rimangono disponibili gli ultimi dati caricati." : `Rimane la revisione ${snapshot.revision}.`} <button onClick={refresh}>Riprova</button></div>}<Component {...props} /></>;
  }
  ConfigurationBoundary.displayName = `LogisticaConfiguration(${Component.name})`;
  return ConfigurationBoundary;
}
